import React, { useState } from "react";
import { ArrowRight, ArrowDown, Maximize2 } from "lucide-react";
import Lightbox from "../Lightbox";
import "./WorkflowSection.css";

const STEPS = [
  {
    num: "01",
    title: "Pièce / besoin initial",
    caption: "Ensemble en atelier — visuel illustratif",
    note: "Visuel illustratif",
    detail:
      "Pièce existante, croquis, plan ancien ou simple besoin exprimé. Analyse de l'existant, relevé des cotes et des interfaces utiles.",
    img: {
      src: "/img/portique-photo-full.webp",
      srcSet: "/img/portique-photo-480.webp 480w, /img/portique-photo-full.webp 560w",
      alt: "Portique mécano-soudé sur roues dans un atelier",
      width: 560,
      height: 396,
    },
  },
  {
    num: "02",
    title: "Modélisation 3D",
    caption: "Rendu CAO — reconstruction et conception",
    note: "Rendu CAO illustratif",
    detail:
      "Reconstruction ou conception du modèle 3D : géométrie, interfaces, renforts et éléments standards, dans une logique de fabrication.",
    img: {
      src: "/img/portique-cao-full.webp",
      srcSet: "/img/portique-cao-480.webp 480w, /img/portique-cao-full.webp 495w",
      alt: "Modèle CAO du portique mécano-soudé",
      width: 495,
      height: 415,
    },
  },
  {
    num: "03",
    title: "Plan / dossier technique",
    caption: "Aperçu de plan — cotes indicatives, non validé pour la fabrication",
    note: "Aperçu de plan — cotes indicatives, non validé pour la fabrication",
    detail:
      "Mise en plan, nomenclature et exports (PDF, DXF, STEP) directement exploitables par l'atelier ou le sous-traitant.",
    illustrative: true,
    img: {
      src: "/img/portique-plan-full.webp",
      srcSet: "/img/portique-plan-480.webp 480w, /img/portique-plan-full.webp 680w",
      alt: "Aperçu de plan du portique : vue de face, vue de côté et détail d'embase",
      width: 680,
      height: 228,
    },
  },
];

const ITEMS = STEPS.map((s) => ({ src: s.img.src, alt: s.img.alt, label: `${s.num} · ${s.title}`, note: s.note, caption: "Portique mécano-soudé — même ensemble du besoin au plan" }));

const WorkflowSection = () => {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(null);
  const step = STEPS[active];

  return (
    <section className="workflow s-section" id="methode" aria-labelledby="methode-title" data-testid="workflow-section">
      <div className="s-container workflow-inner">
        <div className="workflow-copy">
          <p className="s-label is-inverse reveal">Méthode</p>
          <h2 id="methode-title" className="s-title workflow-title reveal" data-delay="1">
            Du modèle 3D au plan de fabrication
          </h2>
          <p className="workflow-lead reveal" data-delay="2">
            Un même ensemble — un portique mécano-soudé — suivi en trois étapes, du besoin initial au dossier
            exploitable. Chaque visuel peut être agrandi.
          </p>

          <div className="workflow-detail reveal" data-delay="3" aria-live="polite" data-testid="workflow-detail">
            <span className="workflow-detail-num">{step.num}</span>
            <div>
              <h3>{step.title}</h3>
              <p>{step.detail}</p>
            </div>
          </div>
        </div>

        <div className="workflow-steps-wrap">
          <ol className="workflow-steps reveal" data-delay="1" data-testid="workflow-steps">
            {STEPS.map((s, i) => (
              <li key={s.num} className="workflow-step-item">
                <button
                  type="button"
                  className={`workflow-step ${i === active ? "is-active" : ""}`}
                  onClick={() => { setActive(i); setZoom(i); }}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  aria-pressed={i === active}
                  aria-label={`${s.title} — agrandir le visuel`}
                  data-testid={`workflow-step-${s.num}`}
                >
                  <span className="workflow-step-media">
                    <span className="workflow-step-num">{s.num}</span>
                    <img
                      src={s.img.src}
                      srcSet={s.img.srcSet}
                      sizes="(max-width: 640px) 88vw, (max-width: 1100px) 40vw, 260px"
                      alt={s.img.alt}
                      loading="lazy"
                      width={s.img.width}
                      height={s.img.height}
                      className={`img-cover ${s.illustrative ? "is-plan" : ""}`}
                    />
                    <span className="workflow-zoom"><Maximize2 size={14} /></span>
                    {s.illustrative && <span className="workflow-illustrative">Aperçu de plan — non validé pour la fabrication</span>}
                  </span>
                  <span className="workflow-step-title">{s.title}</span>
                  <span className="workflow-step-caption">{s.caption}</span>
                  <span className="workflow-step-detail">{s.detail}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <span className="workflow-arrow" aria-hidden="true">
                    <ArrowRight className="workflow-arrow-h" size={22} strokeWidth={1.8} />
                    <ArrowDown className="workflow-arrow-v" size={22} strokeWidth={1.8} />
                  </span>
                )}
              </li>
            ))}
          </ol>

          <p className="workflow-note reveal" data-delay="2" data-testid="workflow-note">
            Étude type — visuels illustratifs du même ensemble. Le plan présenté est un aperçu : ses cotes sont
            indicatives et ne constituent pas des données de fabrication validées.
          </p>
        </div>

        <ul className="workflow-side" aria-hidden="true">
          <li>Précision</li>
          <li>Lisibilité</li>
          <li>Fabrication</li>
        </ul>
      </div>

      {zoom !== null && <Lightbox items={ITEMS} index={zoom} onClose={() => setZoom(null)} />}
    </section>
  );
};

export default WorkflowSection;
